import React from 'react';

const TermsOfServiceContent = () => {
  return (
    <>
      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">1. Agreement to Terms</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          By accessing or using AirBrief at <strong>www.air-brief.com</strong> (the "Service"), you agree to be 
          bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access 
          or use the Service.
        </p>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          AirBrief provides ATPL exam preparation, DLR psychometric assessment training, and related educational 
          games and study tools.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2. Account Registration</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">To use certain features of the Service, you must register for an account. You agree to:</p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Provide accurate and complete information during registration</li>
          <li>Maintain the security and confidentiality of your account credentials</li>
          <li>Notify us immediately of any unauthorized use of your account</li>
          <li>Accept responsibility for all activities that occur under your account</li>
        </ul>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-2">
          We reserve the right to suspend or terminate accounts that violate these Terms.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">3. Subscriptions & Purchases</h2>
        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">3.1 Paid Services</h3>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Certain features of the Service require a paid subscription or one-time purchase. By purchasing 
          a subscription or course access, you agree to pay all applicable fees as described at the time 
          of purchase.
        </p>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">3.2 Subscription Duration</h3>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Subscriptions provide access for a specified period (e.g., 3 months, 6 months, or 12 months). 
          Access to the subscribed content expires at the end of the subscription period unless renewed.
        </p>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">3.3 Coupons & Discounts</h3>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Promotional coupons are subject to their specific terms, including expiration dates, usage limits, 
          and applicable products. Coupons cannot be combined unless explicitly stated and have no cash value.
        </p>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">3.4 Refund Policy</h3>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Due to the digital nature of our products, all sales are final. Refunds may be issued at our 
          sole discretion on a case-by-case basis. If you believe you are entitled to a refund, please 
          contact our support team within 7 days of purchase.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4. Acceptable Use</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">You agree <strong>not</strong> to:</p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Share your account credentials with others or allow multiple people to use a single account</li>
          <li>Copy, redistribute, or reproduce any exam questions, study materials, or content from the Service</li>
          <li>Use automated tools, bots, or scripts to access the Service</li>
          <li>Attempt to bypass device limits, subscription restrictions, or security measures</li>
          <li>Reverse-engineer, decompile, or disassemble any part of the Service</li>
          <li>Use the Service for any unlawful purpose or in violation of any applicable regulations</li>
          <li>Interfere with or disrupt the Service or servers connected to the Service</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5. Device Limits</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          To prevent unauthorized sharing, accounts are limited to a specific number of active devices. 
          You may manage your active devices through your account settings. We reserve the right to 
          restrict or suspend accounts that exceed device limits or show signs of credential sharing.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">6. Intellectual Property</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          All content on the Service — including but not limited to exam questions, study materials, 
          game designs, graphics, software, and text — is the property of AirBrief or its content 
          licensors and is protected by intellectual property laws.
        </p>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Your subscription grants you a <strong>personal, non-transferable, non-exclusive, revocable license</strong> 
          to access and use the content for your own private study purposes only.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">7. Exam Disclaimer</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          AirBrief is an independent study and preparation tool. We are <strong>not</strong> affiliated with, 
          endorsed by, or officially connected to any aviation authority (EASA, FAA, GCAA, DLR, or others).
        </p>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Our materials are designed to aid in exam preparation but do not guarantee success in any 
          examination. Exam content and requirements may change, and it is your responsibility to 
          verify current exam specifications with the relevant authority.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">8. Service Availability</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We strive to keep the Service available at all times, but we do not guarantee uninterrupted or 
          error-free operation. We may temporarily suspend the Service for maintenance, updates, or 
          other reasons without prior notice.
        </p>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We reserve the right to modify, update, or discontinue any feature of the Service at any time.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">9. Limitation of Liability</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          To the fullest extent permitted by law, AirBrief and its operators shall not be liable for 
          any indirect, incidental, special, consequential, or punitive damages, including but not 
          limited to loss of data, loss of revenue, or exam failure, arising from your use of the Service.
        </p>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Our total liability to you for any claims arising from or related to the Service shall not 
          exceed the amount you have paid us in the 12 months preceding the claim.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">10. Account Termination</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We reserve the right to suspend or terminate your account at any time if we believe you have 
          violated these Terms. Upon termination:
        </p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Your access to paid content will be revoked immediately</li>
          <li>No refund will be issued for remaining subscription time</li>
          <li>Your data may be retained or deleted in accordance with our Privacy Policy</li>
        </ul>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-2">
          You may delete your own account at any time through your profile settings or by contacting support.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">11. Changes to Terms</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We may revise these Terms at any time by posting an updated version on this page. Your continued 
          use of the Service after any changes constitutes your acceptance of the new Terms. We encourage 
          you to review these Terms periodically.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">12. Governing Law</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          These Terms shall be governed by and construed in accordance with applicable laws. Any disputes 
          arising from these Terms or your use of the Service shall be resolved through good-faith 
          negotiation before pursuing formal legal action.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">13. Contact Us</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          If you have any questions about these Terms, please contact us:
        </p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Via the Support section in your account</li>
          <li>Email: <strong>support@air-brief.com</strong></li>
        </ul>
      </section>
    </>
  );
};

export default TermsOfServiceContent;
